import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  InputAdornment,
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { authToken } from '../globals/Global';
import {
  getProducts,
  addProductCategory,
  updateProductCategory,
  deleteProductCategory,
  addProductSubCategory,
  updateProductSubCategory,
  deleteProductSubCategory,
} from '../globals/Services';

// ─── Helpers ─────────────────────────────────────────────────────
const getToken = () => authToken || localStorage.getItem('sessionToken');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });
const jsonHeader = () => ({ 'Content-Type': 'application/json', ...authHeader() });

const EMPTY_CATEGORY = { name: '', imageUrl: '', status: 'ACTIVE' };
const EMPTY_SUBCATEGORY = {
  productCategoryId: '',
  name: '',
  description: '',
  images: '',
  logoImageUrl: '',
  status: 'ACTIVE',
  remarks: '',
  price: '',
  offerPrice: '',
};

// ─── SubFields — defined OUTSIDE ProductManagement to prevent remount on every render ───
const SubFields = ({ state, setState, categories }) => (
  <>
    <FormControl fullWidth margin="normal">
      <InputLabel>Category</InputLabel>
      <Select
        value={state.productCategoryId}
        label="Category"
        onChange={e => setState({ ...state, productCategoryId: e.target.value })}
      >
        {categories.map(c => (
          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
        ))}
      </Select>
    </FormControl>

    <TextField
      fullWidth label="Name" margin="normal"
      value={state.name}
      onChange={e => setState({ ...state, name: e.target.value })}
    />

    <TextField
      fullWidth label="Description" multiline rows={3} margin="normal"
      value={state.description}
      onChange={e => setState({ ...state, description: e.target.value })}
    />

    <TextField
      fullWidth label="Images (comma-separated URLs)" margin="normal"
      value={Array.isArray(state.images) ? state.images.join(', ') : state.images}
      onChange={e => setState({ ...state, images: e.target.value })}
      helperText="Enter one or more image URLs separated by commas"
    />

    <TextField
      fullWidth label="Logo Image URL" margin="normal"
      value={state.logoImageUrl}
      onChange={e => setState({ ...state, logoImageUrl: e.target.value })}
    />

    <Box display="flex" gap={2}>
      <TextField
        fullWidth label="Price" type="number" margin="normal"
        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
        value={state.price}
        onChange={e => setState({ ...state, price: e.target.value })}
      />
      <TextField
        fullWidth label="Offer Price" type="number" margin="normal"
        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
        value={state.offerPrice}
        onChange={e => setState({ ...state, offerPrice: e.target.value })}
      />
    </Box>

    <TextField
      fullWidth label="Remarks" margin="normal"
      value={state.remarks}
      onChange={e => setState({ ...state, remarks: e.target.value })}
    />

    <FormControl fullWidth margin="normal">
      <InputLabel>Status</InputLabel>
      <Select
        value={state.status} label="Status"
        onChange={e => setState({ ...state, status: e.target.value })}
      >
        <MenuItem value="ACTIVE">Active</MenuItem>
        <MenuItem value="INACTIVE">Inactive</MenuItem>
      </Select>
    </FormControl>
  </>
);

// ─── Main Component ───────────────────────────────────────────────
export default function ProductManagement() {
  const [categories, setCategories]                 = useState([]);
  const [subCategories, setSubCategories]           = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [refetch, setRefetch]                       = useState(0);
  const triggerRefetch = () => setRefetch(p => p + 1);

  // Dialog open flags
  const [dialogs, setDialogs] = useState({
    addCat: false, editCat: false, deleteCat: false, previewCat: false,
    addSub: false, editSub: false, deleteSub: false, previewSub: false,
  });
  const openDialog  = (key) => setDialogs(d => ({ ...d, [key]: true }));
  const closeDialog = (key) => setDialogs(d => ({ ...d, [key]: false }));

  // Form state
  const [newCategory,    setNewCategory]    = useState(EMPTY_CATEGORY);
  const [editingCat,     setEditingCat]     = useState(null);
  const [deletingCat,    setDeletingCat]    = useState(null);
  const [newSubCategory, setNewSubCategory] = useState(EMPTY_SUBCATEGORY);
  const [editingSub,     setEditingSub]     = useState(null);
  const [deletingSub,    setDeletingSub]    = useState(null);

  // Preview state
  const [previewCat, setPreviewCat] = useState(null);
  const [previewSub, setPreviewSub] = useState(null);

  // ─── Fetch all categories then their subcategories ─────────────
  const fetchData = useCallback(async () => {
    try {
      const knownIds = categories.length > 0
        ? categories.map(c => c.id)
        : Array.from({ length: 20 }, (_, i) => i + 1);

      const results = await Promise.allSettled(
        knownIds.map(id =>
          fetch(getProducts(id), { headers: authHeader() }).then(r => r.ok ? r.json() : null)
        )
      );

      const seen = new Set();
      const cats = [];
      const subs = [];

      results.forEach(r => {
        if (r.status !== 'fulfilled' || !r.value?.data) return;
        r.value.data.forEach(cat => {
          if (!seen.has(cat.productCategoryId)) {
            seen.add(cat.productCategoryId);
            cats.push({
              id:       cat.productCategoryId,
              name:     cat.name,
              imageUrl: cat.imageUrl || '',
              status:   cat.status || 'ACTIVE',
            });
          }
          (cat.productSubcategories || []).forEach(sub => {
            subs.push({
              id:                sub.productSubcategoryId,
              productCategoryId: cat.productCategoryId,
              name:              sub.name,
              description:       sub.description || '',
              images:            sub.images || [],
              logoImageUrl:      sub.logoImageUrl || '',
              status:            sub.status || 'ACTIVE',
              remarks:           sub.remarks || '',
              price:             sub.price,
              offerPrice:        sub.offerPrice,
            });
          });
        });
      });

      setCategories(cats);
      setSubCategories(subs);

      if (!selectedCategoryId && cats.length > 0) setSelectedCategoryId(cats[0].id);
      if (cats.length > 0) {
        setNewSubCategory(p => ({ ...p, productCategoryId: p.productCategoryId || cats[0].id }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Error fetching product data');
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => { fetchData(); }, [refetch]); // eslint-disable-line

  // ─── Category Handlers ─────────────────────────────────────────
  const handleAddCategory = async () => {
    try {
      const res = await fetch(addProductCategory, {
        method: 'POST', headers: jsonHeader(),
        body: JSON.stringify(newCategory),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errorMessage || 'Failed to add category');
      toast.success('Category added!');
      closeDialog('addCat');
      setNewCategory(EMPTY_CATEGORY);
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleUpdateCategory = async () => {
    if (!editingCat) return;
    try {
      const res = await fetch(updateProductCategory, {
        method: 'POST', headers: jsonHeader(),
        body: JSON.stringify({
          id: editingCat.id,
          name: editingCat.name,
          imageUrl: editingCat.imageUrl,
          status: editingCat.status,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errorMessage || 'Failed to update category');
      toast.success('Category updated!');
      closeDialog('editCat');
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCat) return;
    try {
      const res = await fetch(deleteProductCategory(deletingCat.id), {
        method: 'DELETE', headers: authHeader(),
      });
      if (!res.ok) throw new Error('Failed to delete category');
      toast.success('Category deleted!');
      closeDialog('deleteCat');
      setSelectedCategoryId(null);
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  // ─── Subcategory Handlers ──────────────────────────────────────
  const parseImages = (val) =>
    typeof val === 'string'
      ? val.split(',').map(s => s.trim()).filter(Boolean)
      : val;

  const handleAddSubCategory = async () => {
    try {
      const payload = {
        ...newSubCategory,
        productCategoryId: Number(newSubCategory.productCategoryId),
        images: parseImages(newSubCategory.images),
        price: Number(newSubCategory.price),
        offerPrice: Number(newSubCategory.offerPrice),
      };
      const res = await fetch(addProductSubCategory, {
        method: 'POST', headers: jsonHeader(),
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errorMessage || 'Failed to add sub-category');
      toast.success('Sub-category added!');
      closeDialog('addSub');
      setNewSubCategory({ ...EMPTY_SUBCATEGORY, productCategoryId: newSubCategory.productCategoryId });
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleUpdateSubCategory = async () => {
    if (!editingSub) return;
    try {
      const payload = {
        id: editingSub.id,
        productCategoryId: Number(editingSub.productCategoryId),
        name: editingSub.name,
        description: editingSub.description,
        images: parseImages(editingSub.images),
        logoImageUrl: editingSub.logoImageUrl,
        status: editingSub.status,
        remarks: editingSub.remarks,
        price: Number(editingSub.price),
        offerPrice: Number(editingSub.offerPrice),
      };
      const res = await fetch(updateProductSubCategory, {
        method: 'POST', headers: jsonHeader(),
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errorMessage || 'Failed to update sub-category');
      toast.success('Sub-category updated!');
      closeDialog('editSub');
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleDeleteSubCategory = async () => {
    if (!deletingSub) return;
    try {
      const res = await fetch(deleteProductSubCategory(deletingSub.id), {
        method: 'DELETE', headers: authHeader(),
      });
      if (!res.ok) throw new Error('Failed to delete sub-category');
      toast.success('Sub-category deleted!');
      closeDialog('deleteSub');
      triggerRefetch();
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  // ─── Derived ──────────────────────────────────────────────────
  const filteredSubs = selectedCategoryId
    ? subCategories.filter(s => String(s.productCategoryId) === String(selectedCategoryId))
    : [];

  const getSubCount = (catId) =>
    subCategories.filter(s => String(s.productCategoryId) === String(catId)).length;

  const statusColor = (status) =>
    (status || '').toUpperCase() === 'ACTIVE' ? 'success' : 'default';

  // ─── Render ───────────────────────────────────────────────────
  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">Product Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained" color="primary"
            onClick={() => openDialog('addCat')}
            sx={{ minWidth: 180, fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add Category
          </Button>
          <Button
            variant="contained" color="secondary"
            onClick={() => openDialog('addSub')}
            disabled={categories.length === 0}
            sx={{ minWidth: 180, fontWeight: 'bold', textTransform: 'none' }}
          >
            + Add Sub-Category
          </Button>
        </Box>
      </Box>

      {/* Main Grid */}
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 100px)' }}>

        {/* Left — Categories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Product Categories</Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {categories.length > 0 ? categories.map(cat => (
                  <Box
                    key={cat.id}
                    onClick={() => setSelectedCategoryId(cat.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1,
                      border: 1, borderRadius: 2,
                      borderColor: selectedCategoryId === cat.id ? 'primary.main' : 'grey.300',
                      backgroundColor: selectedCategoryId === cat.id ? 'primary.lightest' : 'white',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.light', backgroundColor: 'grey.50' },
                    }}
                  >
                    <img
                      src={cat.imageUrl || 'https://via.placeholder.com/60'}
                      alt={cat.name}
                      style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }}
                    />
                    <Box flexGrow={1}>
                      <Typography variant="subtitle1" fontWeight="bold">{cat.name}</Typography>
                      <Typography variant="body2" color="text.secondary">ID: {cat.id}</Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Chip label={cat.status} color={statusColor(cat.status)} size="small" />
                        <Typography variant="caption" color="primary">
                          {getSubCount(cat.id)} product(s)
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <IconButton size="small" color="info"
                        onClick={e => { e.stopPropagation(); setPreviewCat(cat); openDialog('previewCat'); }}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" color="primary"
                        onClick={e => { e.stopPropagation(); setEditingCat(cat); openDialog('editCat'); }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error"
                        onClick={e => { e.stopPropagation(); setDeletingCat(cat); openDialog('deleteCat'); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )) : (
                  <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    No product categories found.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right — Sub-Categories */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Products / Sub-Categories</Typography>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {filteredSubs.length > 0 ? filteredSubs.map(sub => (
                  <Box
                    key={sub.id}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, mb: 1,
                      border: 1, borderRadius: 2, borderColor: 'grey.200',
                    }}
                  >
                    <img
                      src={(sub.images && sub.images[0]) || sub.logoImageUrl || 'https://via.placeholder.com/80x60'}
                      alt={sub.name}
                      style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                    />
                    <Box flexGrow={1} minWidth={0}>
                      <Typography variant="subtitle1" fontWeight="bold" color="secondary.dark">{sub.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">ID: {sub.id}</Typography>
                      <Box display="flex" gap={1} mt={0.5} mb={0.5}>
                        <Chip label={sub.status} color={statusColor(sub.status)} size="small" />
                        {sub.offerPrice < sub.price && (
                          <Chip
                            label={`${Math.round((1 - sub.offerPrice / sub.price) * 100)}% off`}
                            color="warning" size="small"
                          />
                        )}
                      </Box>
                      <Box display="flex" gap={2}>
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          ₹{sub.price}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          ₹{sub.offerPrice}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {(sub.description || '').split(' ').slice(0, 12).join(' ')}
                        {(sub.description || '').split(' ').length > 12 ? '...' : ''}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <IconButton size="small" color="info"
                        onClick={() => { setPreviewSub(sub); openDialog('previewSub'); }}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" color="primary"
                        onClick={() => {
                          setEditingSub({
                            ...sub,
                            images: Array.isArray(sub.images) ? sub.images.join(', ') : sub.images,
                          });
                          openDialog('editSub');
                        }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error"
                        onClick={() => { setDeletingSub(sub); openDialog('deleteSub'); }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )) : (
                  <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    {selectedCategoryId ? 'No products in this category.' : 'Please select a category.'}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── Add Category Dialog ── */}
      <Dialog open={dialogs.addCat} onClose={() => closeDialog('addCat')} fullWidth maxWidth="sm">
        <DialogTitle>Add Product Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Category Name" margin="normal"
            value={newCategory.name}
            onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
          />
          <TextField
            fullWidth label="Image URL" margin="normal"
            value={newCategory.imageUrl}
            onChange={e => setNewCategory({ ...newCategory, imageUrl: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={newCategory.status} label="Status"
              onChange={e => setNewCategory({ ...newCategory, status: e.target.value })}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDialog('addCat')}>Cancel</Button>
          <Button onClick={handleAddCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Category Dialog ── */}
      <Dialog open={dialogs.editCat} onClose={() => closeDialog('editCat')} fullWidth maxWidth="sm">
        <DialogTitle>Edit Product Category</DialogTitle>
        {editingCat && (
          <>
            <DialogContent>
              <TextField
                fullWidth label="Category Name" margin="normal"
                value={editingCat.name}
                onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
              />
              <TextField
                fullWidth label="Image URL" margin="normal"
                value={editingCat.imageUrl}
                onChange={e => setEditingCat({ ...editingCat, imageUrl: e.target.value })}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editingCat.status} label="Status"
                  onChange={e => setEditingCat({ ...editingCat, status: e.target.value })}
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => closeDialog('editCat')}>Cancel</Button>
              <Button onClick={handleUpdateCategory} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Delete Category Dialog ── */}
      <Dialog open={dialogs.deleteCat} onClose={() => closeDialog('deleteCat')} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete: <strong>{deletingCat?.name}</strong>?
          </Typography>
          <Typography color="error" variant="body2">
            All associated sub-categories will also be deleted. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDialog('deleteCat')}>Cancel</Button>
          <Button onClick={handleDeleteCategory} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Sub-Category Dialog ── */}
      <Dialog open={dialogs.addSub} onClose={() => closeDialog('addSub')} fullWidth maxWidth="sm">
        <DialogTitle>Add Product Sub-Category</DialogTitle>
        <DialogContent>
          <SubFields
            state={newSubCategory}
            setState={setNewSubCategory}
            categories={categories}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDialog('addSub')}>Cancel</Button>
          <Button onClick={handleAddSubCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Sub-Category Dialog ── */}
      <Dialog open={dialogs.editSub} onClose={() => closeDialog('editSub')} fullWidth maxWidth="sm">
        <DialogTitle>Edit Product Sub-Category</DialogTitle>
        {editingSub && (
          <>
            <DialogContent>
              <SubFields
                state={editingSub}
                setState={setEditingSub}
                categories={categories}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => closeDialog('editSub')}>Cancel</Button>
              <Button onClick={handleUpdateSubCategory} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Delete Sub-Category Dialog ── */}
      <Dialog open={dialogs.deleteSub} onClose={() => closeDialog('deleteSub')} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete: <strong>{deletingSub?.name}</strong>?
          </Typography>
          <Typography color="error" variant="body2">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDialog('deleteSub')}>Cancel</Button>
          <Button onClick={handleDeleteSubCategory} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Preview Category Dialog ── */}
      <Dialog open={dialogs.previewCat} onClose={() => closeDialog('previewCat')} fullWidth maxWidth="xs">
        <DialogTitle>Category Preview</DialogTitle>
        {previewCat && (
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={1}>
              <img
                src={previewCat.imageUrl || 'https://via.placeholder.com/120'}
                alt={previewCat.name}
                style={{ width: 120, height: 120, borderRadius: 12, objectFit: 'cover' }}
              />
              <Typography variant="h6" fontWeight="bold">{previewCat.name}</Typography>
              <Box display="flex" gap={2}>
                <Typography variant="body2" color="text.secondary">ID: {previewCat.id}</Typography>
                <Chip label={previewCat.status} color={statusColor(previewCat.status)} size="small" />
              </Box>
              <Typography variant="body2" color="primary">{getSubCount(previewCat.id)} product(s)</Typography>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => closeDialog('previewCat')}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Preview Sub-Category Dialog ── */}
      <Dialog open={dialogs.previewSub} onClose={() => closeDialog('previewSub')} fullWidth maxWidth="sm">
        <DialogTitle>Product Preview</DialogTitle>
        {previewSub && (
          <DialogContent>
            {previewSub.images && previewSub.images.length > 0 && (
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                {previewSub.images.map((img, i) => (
                  <img
                    key={i} src={img} alt={`img-${i}`}
                    style={{ width: 90, height: 70, borderRadius: 8, objectFit: 'cover' }}
                  />
                ))}
              </Box>
            )}
            <Typography variant="h6" fontWeight="bold">{previewSub.name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              ID: {previewSub.id}
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <Chip label={previewSub.status} color={statusColor(previewSub.status)} size="small" />
              {previewSub.offerPrice < previewSub.price && (
                <Chip
                  label={`${Math.round((1 - previewSub.offerPrice / previewSub.price) * 100)}% off`}
                  color="warning" size="small"
                />
              )}
            </Box>
            <Box display="flex" gap={2} mb={1}>
              <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ₹{previewSub.price}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                ₹{previewSub.offerPrice}
              </Typography>
            </Box>
            {previewSub.description && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {previewSub.description}
              </Typography>
            )}
            {previewSub.remarks && (
              <Typography variant="body2">
                <strong>Remarks:</strong> {previewSub.remarks}
              </Typography>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => closeDialog('previewSub')}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position="top-right" autoClose={3000} hideProgressBar={false}
        newestOnTop={false} closeOnClick rtl={false}
        pauseOnFocusLoss draggable pauseOnHover theme="light"
      />
    </Box>
  );
}